import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Printer, ArrowLeft, MessageSquare } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { getBillById, getSettings } from "@/lib/api";
import { amountToWords } from "@/lib/amountWords";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/09a62dc9-6a9e-4bad-b528-41b496ab2382/images/cbcacb73a9857c56eaa830e6342d9e2d9c033cf8fd7db1c24131862abae98730.png";

// ── Full Invoice (A4) ──
function FullInvoice({ bill, settings }) {
  return (
    <div className="printable-invoice max-w-[800px] mx-auto bg-white border border-zinc-300 p-8 font-mono text-sm" data-testid="full-invoice">
      <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-4">
        <div className="flex items-start gap-4">
          <img src={LOGO_URL} alt="Logo" className="h-12 w-12 object-contain" />
          <div>
            <h2 className="text-xl font-black font-heading tracking-tight">{settings.companyName || "SUPERFLAME GAS SERVICES"}</h2>
            <p className="text-xs text-zinc-600 mt-1">{settings.address}</p>
            <p className="text-xs text-zinc-600">Phone: {settings.phone}</p>
          </div>
        </div>
        <div className="text-right border border-zinc-400 p-3 bg-zinc-50">
          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Pay via UPI</p>
          <QRCodeSVG value={`upi://pay?pa=${settings.upiId || "superflame@paytm"}&pn=${encodeURIComponent(settings.companyName || "Superflame Gas")}&am=${bill.netPayable?.toFixed(2)}&tn=${encodeURIComponent(`SF ${bill.flatNo || bill.plotNo || ""} ${bill.invoiceNo} ${bill.customerName}`.trim())}&cu=INR`} size={80} level="M" className="mx-auto mb-1" />
          <p className="text-[10px] font-mono font-semibold">{settings.upiId || "superflame@paytm"}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Invoice No</p><p className="font-semibold">{bill.invoiceNo}</p></div>
        <div className="text-right"><p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Bill Date</p><p className="font-semibold">{new Date(bill.billDate).toLocaleDateString("en-IN")}</p></div>
        <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Due Date</p><p className="font-semibold text-red-600">{new Date(bill.dueDate).toLocaleDateString("en-IN")}</p></div>
        <div className="text-right"><p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Connection Type</p><p className="font-semibold">{bill.connectionType}</p></div>
      </div>
      <div className="border border-black p-3 mb-4 bg-zinc-50">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">Consumer Details</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <p><span className="text-zinc-500">Name:</span> <span className="font-semibold">{bill.customerName}</span></p>
          <p><span className="text-zinc-500">Consumer No:</span> <span className="font-semibold">{bill.consumerNumber}</span></p>
          <p><span className="text-zinc-500">Society:</span> {bill.society}</p>
          <p><span className="text-zinc-500">Flat/Plot:</span> {bill.flatNo || bill.plotNo}</p>
          {bill.towerNo && <p><span className="text-zinc-500">Tower:</span> {bill.towerNo}</p>}
          <p><span className="text-zinc-500">Phone:</span> {bill.phone}</p>
        </div>
      </div>
      <table className="w-full border-collapse border border-black mb-4 text-xs">
        <thead><tr className="bg-zinc-100"><th className="border border-black p-2 text-left font-bold uppercase tracking-wider">Description</th><th className="border border-black p-2 text-right font-bold uppercase tracking-wider">Value</th></tr></thead>
        <tbody>
          <tr><td className="border border-black p-2">Previous Reading</td><td className="border border-black p-2 text-right font-semibold">{(bill.previousReading || 0).toFixed(2)}</td></tr>
          <tr><td className="border border-black p-2">Current Reading</td><td className="border border-black p-2 text-right font-semibold">{(bill.currentReading || 0).toFixed(2)}</td></tr>
          <tr><td className="border border-black p-2">Consumed Units</td><td className="border border-black p-2 text-right font-semibold">{(bill.consumedUnits || 0).toFixed(2)}</td></tr>
          <tr><td className="border border-black p-2">Conversion Factor</td><td className="border border-black p-2 text-right font-semibold">{bill.conversionFactor}</td></tr>
          <tr><td className="border border-black p-2">Unit Cons (KG/SCM)</td><td className="border border-black p-2 text-right font-semibold">{bill.unitConsKgScm?.toFixed(2)}</td></tr>
        </tbody>
      </table>
      <table className="w-full border-collapse border border-black mb-4 text-xs">
        <thead><tr className="bg-zinc-100"><th className="border border-black p-2 text-left font-bold uppercase tracking-wider">Charges</th><th className="border border-black p-2 text-right font-bold uppercase tracking-wider">Amount</th></tr></thead>
        <tbody>
          <tr><td className="border border-black p-2">Usage Amount ({bill.unitConsKgScm?.toFixed(2)} x ₹{bill.rate})</td><td className="border border-black p-2 text-right font-semibold">₹{bill.usageAmount?.toFixed(2)}</td></tr>
          <tr><td className="border border-black p-2">Service Charges</td><td className="border border-black p-2 text-right font-semibold">₹{bill.serviceCharges?.toFixed(2)}</td></tr>
          <tr className="bg-orange-50 font-bold"><td className="border border-black p-2 text-base">NET PAYABLE</td><td className="border border-black p-2 text-right text-base text-orange-700">₹{bill.netPayable?.toFixed(2)}</td></tr>
        </tbody>
      </table>
      <p className="text-[10px] italic text-zinc-500 mb-4 -mt-2">{amountToWords(bill.netPayable || 0)}</p>
      <div className="mb-6 text-xs">
        <p><span className="text-zinc-500">Payment Status:</span>{" "}
          <span className={`font-bold ${bill.isPaid ? "text-green-700" : "text-red-600"}`}>
            {bill.isPaid ? `PAID on ${new Date(bill.paidDate).toLocaleDateString("en-IN")}` : "UNPAID"}
          </span>
        </p>
      </div>
      <div className="border-t-2 border-black pt-4 mt-8">
        <div className="flex justify-between items-end">
          <p className="text-[10px] text-zinc-500">Computer generated bill. Contact: {settings.phone}</p>
          <div className="text-right">
            <p className="font-bold text-sm">FOR {settings.companyName || "SUPERFLAME GAS SERVICES"}</p>
            <p className="text-[10px] text-zinc-500 mt-4">Authorized Signatory</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Compact Slip ──
function CompactSlip({ bill, settings }) {
  return (
    <div className="printable-invoice max-w-[500px] mx-auto bg-white border border-zinc-300 p-5 font-mono text-xs" data-testid="compact-slip">
      <div className="text-center border-b border-black pb-2 mb-3">
        <p className="font-black font-heading text-sm tracking-tight">{settings.companyName || "SUPERFLAME GAS SERVICES"}</p>
        <p className="text-[9px] text-zinc-500">{settings.address}</p>
        <p className="text-[9px] text-zinc-500">Ph: {settings.phone}</p>
      </div>
      <div className="flex justify-between mb-2">
        <span className="font-bold">{bill.invoiceNo}</span>
        <span>{new Date(bill.billDate).toLocaleDateString("en-IN")}</span>
      </div>
      <div className="border border-zinc-300 p-2 mb-3 bg-zinc-50">
        <p className="font-semibold">{bill.customerName}</p>
        <p className="text-zinc-500">{bill.consumerNumber} | {bill.society} | {bill.flatNo || bill.plotNo}</p>
      </div>
      <div className="space-y-1 mb-3">
        <div className="flex justify-between"><span className="text-zinc-500">Prev Reading</span><span>{(bill.previousReading || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Curr Reading</span><span>{(bill.currentReading || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Consumed</span><span className="font-semibold">{(bill.consumedUnits || 0).toFixed(2)} units</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">KG/SCM</span><span>{bill.unitConsKgScm?.toFixed(2)}</span></div>
      </div>
      <div className="border-t border-dashed border-zinc-400 pt-2 mb-2 space-y-1">
        <div className="flex justify-between"><span className="text-zinc-500">Usage ({bill.unitConsKgScm?.toFixed(2)} x ₹{bill.rate})</span><span>₹{bill.usageAmount?.toFixed(2)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Service Charges</span><span>₹{bill.serviceCharges?.toFixed(2)}</span></div>
        <div className="flex justify-between font-bold text-sm border-t border-black pt-1 mt-1">
          <span>NET PAYABLE</span><span className="text-orange-700">₹{bill.netPayable?.toFixed(2)}</span>
        </div>
      </div>
      <div className="flex justify-between items-center mt-3 pt-2 border-t border-zinc-300">
        <div>
          <p className="text-[9px] text-zinc-500">Due: <span className="font-semibold text-red-600">{new Date(bill.dueDate).toLocaleDateString("en-IN")}</span></p>
          <p className={`text-[9px] font-bold ${bill.isPaid ? "text-green-700" : "text-red-600"}`}>{bill.isPaid ? "PAID" : "UNPAID"}</p>
        </div>
        <QRCodeSVG value={`upi://pay?pa=${settings.upiId || "superflame@paytm"}&pn=${encodeURIComponent(settings.companyName || "Superflame Gas")}&am=${bill.netPayable?.toFixed(2)}&tn=${encodeURIComponent(`SF ${bill.flatNo || bill.plotNo || ""} ${bill.invoiceNo}`.trim())}&cu=INR`} size={50} level="M" />
      </div>
      <p className="text-center text-[8px] text-zinc-400 mt-3">FOR {settings.companyName || "SUPERFLAME GAS SERVICES"}</p>
    </div>
  );
}

// ── Thermal Receipt (80mm) ──
function ThermalReceipt({ bill, settings }) {
  return (
    <div className="printable-invoice mx-auto bg-white p-3 font-mono" style={{ maxWidth: "302px", fontSize: "11px" }} data-testid="thermal-receipt">
      <div className="text-center mb-2">
        <p className="font-black text-xs tracking-tight">{settings.companyName || "SUPERFLAME GAS SERVICES"}</p>
        <p style={{ fontSize: "8px" }} className="text-zinc-500">{settings.address}</p>
        <p style={{ fontSize: "8px" }} className="text-zinc-500">Ph: {settings.phone}</p>
        <div className="border-t border-dashed border-black mt-1 mb-1" />
      </div>
      <div className="flex justify-between" style={{ fontSize: "10px" }}>
        <span className="font-bold">{bill.invoiceNo}</span>
        <span>{new Date(bill.billDate).toLocaleDateString("en-IN")}</span>
      </div>
      <div className="border-b border-dashed border-black pb-1 mb-1" style={{ fontSize: "10px" }}>
        <p className="font-semibold">{bill.customerName}</p>
        <p className="text-zinc-500">{bill.consumerNumber}</p>
        <p className="text-zinc-500">{bill.society}, {bill.flatNo || bill.plotNo}</p>
      </div>
      <div className="space-y-0.5 mb-1" style={{ fontSize: "10px" }}>
        <div className="flex justify-between"><span>Prev</span><span>{(bill.previousReading || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Curr</span><span>{(bill.currentReading || 0).toFixed(2)}</span></div>
        <div className="flex justify-between font-semibold"><span>Units</span><span>{(bill.consumedUnits || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>KG/SCM</span><span>{bill.unitConsKgScm?.toFixed(2)}</span></div>
      </div>
      <div className="border-t border-dashed border-black pt-1 space-y-0.5" style={{ fontSize: "10px" }}>
        <div className="flex justify-between"><span>Usage</span><span>₹{bill.usageAmount?.toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Svc Chg</span><span>₹{bill.serviceCharges?.toFixed(2)}</span></div>
        <div className="border-t border-double border-black mt-1 pt-1 flex justify-between font-black text-xs">
          <span>TOTAL</span><span>₹{bill.netPayable?.toFixed(2)}</span>
        </div>
      </div>
      <div className="text-center mt-2">
        <QRCodeSVG value={`upi://pay?pa=${settings.upiId || "superflame@paytm"}&pn=${encodeURIComponent(settings.companyName || "Superflame Gas")}&am=${bill.netPayable?.toFixed(2)}&tn=${encodeURIComponent(`SF ${bill.flatNo || bill.plotNo || ""} ${bill.invoiceNo}`.trim())}&cu=INR`} size={70} level="M" className="mx-auto" />
        <p style={{ fontSize: "8px" }} className="mt-1">{settings.upiId || "superflame@paytm"}</p>
      </div>
      <div className="border-t border-dashed border-black mt-2 pt-1 text-center" style={{ fontSize: "8px" }}>
        <p>Due: <span className="font-bold">{new Date(bill.dueDate).toLocaleDateString("en-IN")}</span> | {bill.isPaid ? "PAID" : "UNPAID"}</p>
        <p className="mt-1">{settings.companyName || "SUPERFLAME GAS SERVICES"}</p>
        <p className="text-zinc-400">Thank you!</p>
      </div>
    </div>
  );
}

// ── Main Component ──
export default function InvoicePreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [settings, setSettings] = useState({});
  const [format, setFormat] = useState("full");

  useEffect(() => {
    getBillById(id).then(setBill);
    getSettings().then(setSettings);
  }, [id]);

  if (!bill) {
    return (
      <div className="p-8 text-center text-zinc-400" data-testid="invoice-not-found">
        <p>Bill not found.</p>
        <Button onClick={() => navigate(-1)} variant="link" className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="invoice-preview-page">
      <div className="no-print flex flex-wrap items-center gap-3 mb-6 max-w-[800px] mx-auto">
        <Button data-testid="back-button" variant="outline" onClick={() => navigate(-1)} className="rounded-sm border-zinc-300">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <Select value={format} onValueChange={setFormat}>
          <SelectTrigger data-testid="print-format-select" className="w-[180px] bg-zinc-50 border-zinc-300 rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="full">Full Invoice (A4)</SelectItem>
            <SelectItem value="compact">Compact Slip</SelectItem>
            <SelectItem value="thermal">Thermal Receipt</SelectItem>
          </SelectContent>
        </Select>

        <Button data-testid="print-button" onClick={() => window.print()} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
          <Printer className="w-4 h-4 mr-2" /> Print
        </Button>

        <Button
          data-testid="send-notification-button"
          variant="outline"
          onClick={() => {
            if (bill?.phone) {
              toast.info(`SMS to ${bill.phone}: Bill ₹${bill.netPayable?.toFixed(2)} due ${new Date(bill.dueDate).toLocaleDateString("en-IN")}`, {
                description: "Configure Twilio in Settings for real SMS",
                duration: 5000,
              });
            } else { toast.error("No phone number on this bill"); }
          }}
          className="rounded-sm border-zinc-300"
        >
          <MessageSquare className="w-4 h-4 mr-2" /> Notify
        </Button>
      </div>

      <div className="print-root">
        {format === "full" && <FullInvoice bill={bill} settings={settings} />}
        {format === "compact" && <CompactSlip bill={bill} settings={settings} />}
        {format === "thermal" && <ThermalReceipt bill={bill} settings={settings} />}
      </div>
    </div>
  );
}
